import { z } from 'zod';
import { CalendarDate, CreateProjectInput, ProjectStatus, TaskPriority, WorkflowCategory, } from './project.js';
/**
 * Đồng bộ dự án với hệ thống quản lý công việc bên ngoài — docs/plan-project-sync.md.
 *
 * Ba tầng, từ trên xuống:
 * - **Nguồn** (`sync_provider`, §3): một hệ thống bên ngoài, cấu hình của bản cài
 *   đặt (`system_config:manage`). Từ APM-SYNC2-a có thể mang bí mật **của app**
 *   (Lark, Meegle — docs/plan-sync-providers.md §2.2), chỉ ghi, không bao giờ đọc ra.
 * - **Kết nối** (`project_sync_connection`, §4.1): một nguồn của một dự án, mang
 *   thông tin xác thực (`SyncCredentials`) và **container** đã chọn (`SyncContainer`
 *   — dự án / board / danh sách bên ngoài).
 * - **Vận hành** (`project_sync_state` / `_link` / `_run`, §4.2): con trỏ, liên
 *   kết id ngoài↔trong, nhật ký từng lượt.
 */
// ── Nguồn (super admin) ─────────────────────────────────────────────────────
/**
 * Loại nguồn. Thêm loại = thêm một giá trị + một adapter (§6.1). Loại chưa có
 * adapter (APM-SYNC2-a: mọi loại trừ `aiservice`) bị từ chối lúc tạo nguồn với
 * `SYNC_PROVIDER_KIND_UNSUPPORTED` — docs/plan-sync-providers.md §0.
 */
export const SyncProviderKind = z.enum([
    'aiservice',
    'jira_cloud',
    'redmine',
    'trello',
    'lark_tasks',
    'meegle',
]);
/**
 * Chu kỳ người dùng chọn được, phút (§5.7). `0` = chỉ chạy tay.
 *
 * Các mốc vì: 60 request/phút/key là trần của bên kia; một lượt không có gì mới
 * tốn **1** request; dưới 5 phút chỉ tốn tải hai bên.
 */
export const SYNC_INTERVAL_MINUTES = [0, 5, 15, 30, 60, 360, 1440];
export const SyncIntervalMinutes = z
    .number()
    .int()
    .refine((n) => SYNC_INTERVAL_MINUTES.includes(n), {
    message: 'Chu kỳ không nằm trong danh sách cho phép',
});
export const DEFAULT_SYNC_INTERVAL_MINUTES = 15;
/**
 * Địa chỉ gốc của một nguồn — chỉ `http(s)`, không user/pass, query hay fragment.
 * `https://` bắt buộc ở production: kiểm ở API (`NODE_ENV`), không ở đây — schema
 * dùng chung không biết môi trường.
 */
const ProviderUrl = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .refine((raw) => {
    try {
        const url = new URL(raw);
        return ((url.protocol === 'https:' || url.protocol === 'http:') &&
            !url.username &&
            !url.password &&
            !url.search &&
            !url.hash);
    }
    catch {
        return false;
    }
}, { message: 'Địa chỉ phải là URL http(s) không có query, fragment hay thông tin đăng nhập' });
// ── Cấu hình nguồn theo loại (APM-SYNC2-a, docs/plan-sync-providers.md §2.2) ──
/** Định dạng chữ của máy chủ Redmine (Administration › Settings › General). */
export const SyncTextFormat = z.enum(['common_mark', 'textile', 'markdown']);
/** Vùng của Lark: bản quốc tế hay Feishu (Trung Quốc). */
export const SyncLarkRegion = z.enum(['larksuite', 'feishu']);
/**
 * `sync_provider.config` — không bí mật, trả ra cho super admin. Mỗi loại chỉ
 * dùng vài khoá (bảng §2.2); `syncProviderShapeIssues` từ chối khoá lạ.
 */
export const SyncProviderConfig = z
    .object({
    textFormat: SyncTextFormat,
    /** CA riêng của máy chủ Redmine nội bộ (PEM). */
    caCertPem: z
        .string()
        .trim()
        .min(1)
        .max(20_000)
        .refine((pem) => pem.includes('-----BEGIN CERTIFICATE-----'), {
        message: 'CA phải ở dạng PEM (-----BEGIN CERTIFICATE-----)',
    }),
    region: SyncLarkRegion,
})
    .partial()
    .strict();
const AppSecretPart = z.string().trim().min(1).max(500);
/**
 * Bí mật **của app** ở cấp nguồn (`sync_provider.app_credentials_enc`): Lark
 * `{appId, appSecret}`, Meegle `{pluginId, pluginSecret}`. Chỉ ghi — API chỉ trả
 * `appCredentialsHint`.
 */
export const SyncAppCredentials = z.union([
    z.object({ appId: AppSecretPart, appSecret: AppSecretPart }).strict(),
    z.object({ pluginId: AppSecretPart, pluginSecret: AppSecretPart }).strict(),
]);
/** Trello có một địa chỉ API duy nhất — nguồn Trello không nhập địa chỉ. */
export const TRELLO_API_BASE_URL = 'https://api.trello.com';
/** Trang web của Trello — link mở card (`c/<shortLink>`) ghép với địa chỉ này (APM-SYNC2-f). */
export const TRELLO_WEB_BASE_URL = 'https://trello.com';
/** Meegle mặc định khi super admin không nhập domain riêng. */
export const MEEGLE_DEFAULT_BASE_URL = 'https://meegle.com';
/** Lark theo vùng (§2.2). */
export const LARK_API_BASE_URL = {
    larksuite: 'https://open.larksuite.com',
    feishu: 'https://open.feishu.cn',
};
/**
 * Trang đăng nhập OAuth của Lark theo vùng (APM-SYNC2-g, §6.2) — người dùng được
 * đưa sang đây, không phải `open.*`.
 */
export const LARK_ACCOUNTS_BASE_URL = {
    larksuite: 'https://accounts.larksuite.com',
    feishu: 'https://accounts.feishu.cn',
};
/** Link mở việc của Lark (`task.url` là applink tuyệt đối) — `web_base_url` mặc định. */
export const LARK_APPLINK_BASE_URL = {
    larksuite: 'https://applink.larksuite.com',
    feishu: 'https://applink.feishu.cn',
};
/** Khoá `config` mỗi loại được dùng — bảng §2.2. */
const CONFIG_KEYS = {
    aiservice: [],
    jira_cloud: [],
    redmine: ['textFormat', 'caCertPem'],
    trello: [],
    lark_tasks: ['region'],
    meegle: [],
};
/**
 * Hình của một nguồn có hợp với loại của nó không (bảng §2.2) — dùng chung cho
 * schema tạo (`superRefine`) và cho API khi sửa (loại nằm ở hàng, không ở body).
 * Lần sửa không gửi `appCredentials` = giữ bí mật cũ, nên truyền
 * `appCredentialsKept` để không đòi nhập lại.
 */
export function syncProviderShapeIssues(kind, shape, opts = {}) {
    const issues = [];
    const base = shape.apiBaseUrl ?? null;
    const config = shape.config ?? {};
    const app = shape.appCredentials ?? null;
    if ((kind === 'aiservice' || kind === 'redmine') && !base) {
        issues.push({ path: 'apiBaseUrl', message: 'Loại nguồn này cần địa chỉ máy chủ' });
    }
    if (kind === 'jira_cloud' && base) {
        issues.push({
            path: 'apiBaseUrl',
            message: 'Jira Cloud không có địa chỉ ở cấp nguồn — site nhập theo từng kết nối',
        });
    }
    if (kind === 'trello' && base && base.replace(/\/+$/, '') !== TRELLO_API_BASE_URL) {
        issues.push({ path: 'apiBaseUrl', message: `Trello chỉ có ${TRELLO_API_BASE_URL}` });
    }
    if (kind === 'lark_tasks' && !config.region) {
        issues.push({ path: 'config.region', message: 'Chọn vùng Lark' });
    }
    for (const key of Object.keys(config)) {
        if (!CONFIG_KEYS[kind].includes(key)) {
            issues.push({ path: `config.${key}`, message: 'Loại nguồn này không dùng trường này' });
        }
    }
    const wantsApp = kind === 'lark_tasks' ? 'appId' : kind === 'meegle' ? 'pluginId' : null;
    if (wantsApp === null && app) {
        issues.push({ path: 'appCredentials', message: 'Loại nguồn này không có bí mật cấp app' });
    }
    else if (wantsApp !== null) {
        if (app && !(wantsApp in app)) {
            issues.push({
                path: 'appCredentials',
                message: wantsApp === 'appId' ? 'Lark cần App ID + App Secret' : 'Meegle cần Plugin ID + Secret',
            });
        }
        else if (!app && !opts.appCredentialsKept) {
            issues.push({ path: 'appCredentials', message: 'Loại nguồn này cần bí mật của app' });
        }
    }
    return issues;
}
/** Hàng ở màn quản trị hệ thống. */
export const SyncProviderView = z.object({
    id: z.uuid(),
    kind: SyncProviderKind,
    name: z.string(),
    /** `null` với loại không có địa chỉ cấp nguồn (Jira Cloud — site theo kết nối). */
    apiBaseUrl: z.string().nullable(),
    webBaseUrl: z.string().nullable(),
    config: SyncProviderConfig.nullable(),
    /** Gợi ý bí mật cấp app (`cli_a1… · ••••9f2c`) — bí mật thật không bao giờ rời server. */
    appCredentialsHint: z.string().nullable(),
    minIntervalMinutes: z.number().int(),
    enabled: z.boolean(),
    /** Số kết nối (kể cả đã ngắt) trỏ tới — > 0 thì không xoá được. */
    connectionCount: z.number().int(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Thứ người tạo dự án thấy (`GET /sync-providers`) — chỉ nguồn đang bật.
 *
 * `minIntervalMinutes` có mặt (APM-SYNC-d) để ô chu kỳ không đưa ra mốc mà lệnh
 * tạo kết nối sẽ từ chối: mặc định 15 phút với một nguồn đặt tối thiểu 30 là
 * 400 sau khi người dùng đã điền xong cả hộp. Không phải bí mật — địa chỉ của
 * nguồn mới là thứ chỉ super admin thấy.
 */
export const SyncProviderOption = SyncProviderView.pick({
    id: true,
    kind: true,
    name: true,
    minIntervalMinutes: true,
}).extend({
    /** APM-SYNC2-a — khả năng của loại nguồn, để giao diện không `switch` theo `kind`. */
    capabilities: z.lazy(() => SyncProviderCapabilities),
});
/**
 * Khả năng của một **loại** nguồn — docs/plan-sync-providers.md §2.3. Trả cùng
 * danh sách nguồn: ô nhập thông tin xác thực, có bước chọn container không,
 * trường nào xám (kèm lý do) — giao diện không `switch` theo `kind` ngoài nhãn.
 */
export const SyncCredentialFieldType = z.enum(['text', 'secret', 'url', 'email']);
export const SyncProviderCapabilities = z.object({
    /** Ô của form thông tin xác thực, theo thứ tự hiện; `key` là khoá trong `SyncCredentials`. */
    credentialFields: z.array(z.object({
        key: z.string(),
        type: SyncCredentialFieldType,
        /** Khoá i18n (`projectSync.credentials.<kind>.<key>`). */
        labelKey: z.string(),
        helpKey: z.string().nullable(),
        required: z.boolean(),
    })),
    /** Kết nối bằng nút "Kết nối bằng Lark" (OAuth) thay vì dán token. */
    oauthConnect: z.boolean(),
    /** Một token thấy nhiều dự án/board ⇒ có bước chọn container. */
    containerPicker: z.boolean(),
    /** Trạng thái ngoài có nhóm todo/đang làm/xong không. */
    statusCategories: z.enum(['native', 'closed_only', 'none']),
    userEmail: z.enum(['always', 'sometimes', 'never']),
    /** Đếm được tổng số việc (tiến trình xác định). */
    totals: z.boolean(),
    multiAssignee: z.boolean(),
    priority: z.enum(['native', 'custom_field', 'none']),
    estimate: z.enum(['native', 'custom_field', 'none']),
    sprint: z.enum(['native', 'none']),
    parent: z.boolean(),
    taskType: z.boolean(),
    statusWrite: z.enum(['direct', 'transition', 'flag_and_section', 'none']),
    commentEdit: z.boolean(),
    maxDescriptionChars: z.number().int().positive().nullable(),
});
/**
 * Dòng tổng "Đồng bộ dự án" ở phần Luồng ngầm của `/system` — APM-SYNC-j
 * (`GET /admin/sync-providers/health`, `system_config:manage` — chỉ super admin,
 * kể cả khi phần còn lại của `/admin/sync-providers` là của admin tổ chức).
 *
 * Đếm kết nối **chưa ngắt** của dự án chưa xoá, theo trạng thái hiển thị
 * (`SyncConnectionStatus`) gộp ba nhóm: `active` = hoạt động / đang nhập lần
 * đầu / đã nhập xong / chờ duyệt dự án; `error` = lỗi / key không còn hợp lệ;
 * `paused` = tạm dừng. Nguồn bị tắt thì mọi kết nối của nó là `disabled`.
 *
 * ⚠️ Không nhận asOf: trạng thái **vận hành** hiện tại của các kết nối — nhật
 * ký vận hành cố ý không nằm trong `event_log` (plan §4.2) nên không có gì để
 * dựng lại ở một mốc quá khứ.
 */
export const SyncProviderHealth = z.object({
    id: z.uuid(),
    kind: SyncProviderKind,
    name: z.string(),
    enabled: z.boolean(),
    /**
     * Tổ chức sở hữu nguồn (APM-ORGINT-b, plan-org-integrations D6): super admin
     * vẫn xem sức khoẻ của **mọi** tổ chức, nhóm theo tổ chức. Danh sách sắp theo
     * `orgName` rồi theo thứ tự tạo nguồn.
     */
    orgId: z.uuid(),
    orgName: z.string(),
    active: z.number().int().nonnegative(),
    error: z.number().int().nonnegative(),
    paused: z.number().int().nonnegative(),
    disabled: z.number().int().nonnegative(),
    /** Tổng kết nối chưa ngắt = bốn số trên cộng lại. */
    total: z.number().int().nonnegative(),
});
export const SyncProvidersHealthView = z.object({
    generatedAt: z.iso.datetime(),
    providers: z.array(SyncProviderHealth),
});
/**
 * Tạo nguồn. Trường nào bắt buộc tuỳ `kind` (bảng §2.2) — kiểm ở
 * `syncProviderShapeIssues`. `appCredentials` chỉ ghi: mã hoá ở API, không bao
 * giờ trả lại.
 */
export const CreateSyncProviderInput = z
    .object({
    kind: SyncProviderKind.default('aiservice'),
    name: z.string().trim().min(1).max(80),
    apiBaseUrl: ProviderUrl.nullable().optional(),
    webBaseUrl: ProviderUrl.nullable().optional(),
    config: SyncProviderConfig.nullable().optional(),
    appCredentials: SyncAppCredentials.nullable().optional(),
    minIntervalMinutes: z.number().int().min(1).max(1440).default(5),
    enabled: z.boolean().default(true),
})
    .superRefine((value, ctx) => {
    for (const issue of syncProviderShapeIssues(value.kind, value)) {
        ctx.addIssue({ code: 'custom', path: issue.path.split('.'), message: issue.message });
    }
});
/**
 * Mọi trường tuỳ chọn; `kind` không đổi được sau khi tạo. Không gửi
 * `appCredentials` = giữ bí mật cũ. Hình theo loại kiểm ở API (loại nằm ở hàng).
 */
export const UpdateSyncProviderInput = z
    .object({
    name: z.string().trim().min(1).max(80),
    apiBaseUrl: ProviderUrl.nullable(),
    webBaseUrl: ProviderUrl.nullable(),
    config: SyncProviderConfig.nullable(),
    appCredentials: SyncAppCredentials,
    minIntervalMinutes: z.number().int().min(1).max(1440),
    enabled: z.boolean(),
})
    .partial();
/**
 * Kết quả nút **Kiểm tra** (§3): `GET <base>/api/public/v1/project` không key,
 * mong 401 với vỏ JSON đúng hình dạng. Sai địa chỉ thì API trả lỗi
 * `SYNC_PROVIDER_UNREACHABLE`, không phải `ok: false`.
 */
export const SyncProviderTestResult = z.object({
    ok: z.literal(true),
    upstreamStatus: z.number().int(),
    latencyMs: z.number().int(),
});
// ── Kết nối (dự án) ─────────────────────────────────────────────────────────
/** §5.1 — hai chiều / chỉ kéo về / chỉ đẩy ra / nhập một lần. */
export const SyncDirection = z.enum(['two_way', 'pull', 'push', 'once']);
/** Q3 — lần đầu kéo về tất cả hay chỉ việc chưa xong. */
export const SyncInitialScope = z.enum(['all', 'open']);
/**
 * Sprint **do đồng bộ tạo** nhận cột nào — APM-SYNC-run (người dùng chốt 2026-10-08).
 *
 * - `all`: bản sao đủ các cột chung của dự án — board nhìn giống hệt bên nguồn,
 *   kể cả cột chưa có việc nào.
 * - `used`: chỉ những cột đang có việc trong sprint ấy. Cột còn thiếu **tự được
 *   thêm** vào sprint ngay khi có việc đầu tiên sang trạng thái đó, nên không
 *   việc nào phải nằm tạm ở cột khác.
 *
 * Sprint tạo tay luôn là `all`: lúc tạo nó chưa có việc nào để biết cột nào "có việc".
 */
export const SyncSprintColumns = z.enum(['all', 'used']);
/**
 * Loại thông tin đồng bộ (§4.1). Trường tắt ⇒ **không có mặt** trong
 * `NormalizedTask` (không so, không ghi). Tag, trường tuỳ chỉnh, giờ đã làm cố
 * ý không có (§1).
 */
export const SYNC_ENTITY_KEYS = [
    'title',
    'description',
    'status',
    'assignee',
    'priority',
    'dates',
    'estimate',
    'sprint',
    'parent',
    'taskType',
    'comments',
];
export const SyncEntities = z.object({
    title: z.boolean(),
    description: z.boolean(),
    status: z.boolean(),
    assignee: z.boolean(),
    priority: z.boolean(),
    dates: z.boolean(),
    estimate: z.boolean(),
    sprint: z.boolean(),
    parent: z.boolean(),
    taskType: z.boolean(),
    comments: z.boolean(),
});
export const DEFAULT_SYNC_ENTITIES = {
    title: true,
    description: true,
    status: true,
    assignee: true,
    priority: true,
    dates: true,
    estimate: true,
    sprint: true,
    parent: true,
    taskType: true,
    comments: true,
};
export const SyncRunKind = z.enum(['pull', 'push', 'initial', 'reconcile']);
export const SyncRunTrigger = z.enum(['schedule', 'manual', 'change']);
export const SyncRunOutcome = z.enum(['ok', 'no_change', 'partial', 'error']);
/** Việc gì xảy ra với một việc trong một lượt (§5.3–§5.5). */
export const SyncRunItemAction = z.enum([
    'created',
    'updated',
    'conflict',
    'pushed',
    'push_failed',
    'delete_unsupported',
    'skipped',
    /** Lượt đối soát tuần (APM-SYNC-j): việc không còn ở nguồn — đánh dấu, không xoá. */
    'missing',
    /** Lượt đối soát: việc từng "không còn ở nguồn" nay thấy lại. */
    'restored',
]);
/** Tối đa bao nhiêu `items` một lượt giữ lại — phần còn lại chỉ còn trong số đếm. */
export const SYNC_RUN_ITEMS_CAP = 50;
/** Khối Điều lệ liệt kê tối đa chừng này việc "không còn ở nguồn" (APM-SYNC-j). */
export const SYNC_MISSING_ITEMS_CAP = 20;
export const SyncRunItem = z.object({
    /** Id việc bên trong, `null` khi chưa tạo được. */
    taskId: z.uuid().nullable(),
    /** Mã việc bên trong (`APM-12`). */
    code: z.string().nullable(),
    /** Mã việc bên ngoài (`WEB-42`). */
    externalCode: z.string().nullable(),
    action: SyncRunItemAction,
    fields: z.array(z.string()),
    /** Mã lỗi từng việc (`DOD_BLOCKED`, `PARENT_PENDING`, `USER_UNMAPPED`…) hoặc thông điệp từ bên kia. */
    error: z.string().optional(),
});
/**
 * Một giá trị của nguồn mà lượt chạy **không ghép chắc chắn** được — APM-SYNC-run
 * (người dùng chốt 2026-10-08: không tự đoán khi khó, để người dùng ghép ở màn
 * kết quả của lượt).
 *
 * Chỉ hai loại có bước đoán bị bỏ: trạng thái (cột nào) và loại việc. Việc mang
 * giá trị ấy vẫn được nhập — trạng thái đặt tạm ở cột mặc định, loại việc tạm
 * dùng loại đầu của dự án — và `tasks` là số việc đang chờ trong lượt này.
 */
export const SyncPendingMapping = z.object({
    kind: z.enum(['status', 'task_type']),
    externalId: z.string(),
    externalName: z.string(),
    tasks: z.number().int().nonnegative(),
});
export const SyncRunSummary = z.object({
    created: z.number().int(),
    updated: z.number().int(),
    conflicts: z.number().int(),
    comments: z.number().int(),
    errors: z.number().int(),
    /** Cắt ở `SYNC_RUN_ITEMS_CAP`. */
    items: z.array(SyncRunItem),
    /** Giá trị nguồn lượt này gặp mà chưa ghép chắc — rỗng/thiếu = không có gì chờ. */
    pending: z.array(SyncPendingMapping).optional(),
    /**
     * Tổng số việc bên nguồn mà lượt **nhập lần đầu** đọc được từ `meta.total` —
     * mẫu số của "Đang nhập lần đầu x/y" ở khối Điều lệ (APM-SYNC-h, §7.3). Vắng
     * ở lượt kéo thường và ở lượt ghi trước APM-SYNC-h.
     */
    sourceTotal: z.number().int().nonnegative().optional(),
    /**
     * Lượt đối soát (APM-SYNC-j): số việc **vừa** đánh dấu "không còn ở nguồn"
     * / số việc thấy lại. Vắng ở các loại lượt khác.
     */
    missing: z.number().int().nonnegative().optional(),
    restored: z.number().int().nonnegative().optional(),
});
export const EMPTY_SYNC_RUN_SUMMARY = {
    created: 0,
    updated: 0,
    conflicts: 0,
    comments: 0,
    errors: 0,
    items: [],
};
export const SyncRunView = z.object({
    id: z.uuid(),
    kind: SyncRunKind,
    trigger: SyncRunTrigger,
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    /** `null` khi đang chạy. */
    outcome: SyncRunOutcome.nullable(),
    requests: z.number().int(),
    summary: SyncRunSummary.nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
});
// ── Tiến trình + nhật ký từng mục (APM-SYNC-k, plan §4.2, §5.7, §7.3) ─────────
/** Bước của lượt nhập lần đầu: danh mục → công việc → bình luận → xong. */
export const SyncPhase = z.enum(['lookups', 'tasks', 'comments', 'done']);
/** Chiều của một dòng nhật ký: về (kéo), ra (đẩy), đối soát tuần. */
export const SyncLogDirection = z.enum(['pull', 'push', 'reconcile']);
export const SyncLogEntity = z.enum(['task', 'comment', 'sprint', 'member', 'status']);
/**
 * Việc gì xảy ra với một mục — cùng bộ với `SyncRunItemAction`, thêm `error`
 * (lỗi lệnh/HTTP làm dừng cả lượt, không gắn với mục nào).
 */
export const SyncLogAction = z.enum([
    'created',
    'updated',
    'conflict',
    'skipped',
    'error',
    'pushed',
    'push_failed',
    'missing',
    'restored',
    'delete_unsupported',
]);
/** Trần một trang nhật ký. */
export const SYNC_LOG_PAGE_MAX = 100;
/** Số lượt gần nhất ở dải lượt chạy của hộp tiến trình. */
export const SYNC_PROGRESS_RUNS = 20;
/** Id dòng nhật ký (BIGINT) đi dạng chuỗi chữ số — vượt `Number.MAX_SAFE_INTEGER` vẫn đúng. */
const SyncLogId = z.string().regex(/^\d{1,20}$/);
/**
 * `GET …/sync-connections/:cid/log` — keyset theo `id`, mới trước. `before` để
 * tải thêm dòng cũ hơn; `after` để **tail trực tiếp** (chỉ dòng mới hơn).
 */
export const SyncLogQuery = z.object({
    before: SyncLogId.optional(),
    after: SyncLogId.optional(),
    limit: z.coerce.number().int().min(1).max(SYNC_LOG_PAGE_MAX).default(50),
    runId: z.uuid().optional(),
    action: SyncLogAction.optional(),
    direction: SyncLogDirection.optional(),
    /** Tìm trong mã ngoài / tên. */
    q: z.string().trim().max(100).optional(),
});
export const SyncLogEntry = z.object({
    id: SyncLogId,
    runId: z.uuid(),
    at: z.iso.datetime(),
    direction: SyncLogDirection,
    entity: SyncLogEntity,
    action: SyncLogAction,
    /** Việc bên trong mà dòng nói tới (cả dòng bình luận); `null` khi chưa tạo được. */
    taskId: z.uuid().nullable(),
    /** Mã việc bên trong (`APM-12`) — `null` khi việc không còn/không có. */
    localCode: z.string().nullable(),
    externalId: z.string().nullable(),
    /** Mã bên nguồn (`WEB-42`). */
    externalCode: z.string().nullable(),
    /** Link mở việc bên nguồn — chỉ khi nguồn có `web_base_url`. */
    externalUrl: z.string().nullable(),
    title: z.string().nullable(),
    /** Tên các trường đổi (`status`, `assignee`…). */
    fields: z.array(z.string()),
    message: z.string().nullable(),
});
export const SyncLogPage = z.object({
    entries: z.array(SyncLogEntry),
    /** Còn dòng cũ hơn dòng cuối (chỉ có nghĩa khi không dùng `after`). */
    hasMore: z.boolean(),
});
/** Một đoạn trên dải lượt chạy — số đếm lấy từ `summary`. */
export const SyncProgressRun = z.object({
    id: z.uuid(),
    kind: SyncRunKind,
    trigger: SyncRunTrigger,
    startedAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    outcome: SyncRunOutcome.nullable(),
    created: z.number().int(),
    updated: z.number().int(),
    conflicts: z.number().int(),
    errors: z.number().int(),
    comments: z.number().int(),
    /** Số giá trị nguồn lượt này để lại chờ ghép (trạng thái + loại việc). */
    pending: z.number().int().nonnegative(),
});
/**
 * `GET …/sync-connections/:cid/progress` — hộp "Tiến trình & nhật ký".
 *
 * `processed`/`total`/`phase`/`startedAt` là của **lượt nhập lần đầu** (có thể
 * trải nhiều job); `total` `null` khi nguồn không cho biết tổng. `ratePerMin` =
 * `processed` / số phút từ `startedAt` (tới lúc xong, nếu đã xong) — tính cả
 * khoảng chờ giữa hai job, cho đúng thực tế. `etaSec` chỉ khi biết `total` và
 * chưa xong.
 */
export const SyncProgressView = z.object({
    /** Đang có lượt giữ khoá. */
    running: z.boolean(),
    /** Lần nhập đầu chưa xong. */
    importing: z.boolean(),
    phase: SyncPhase.nullable(),
    processed: z.number().int().nonnegative(),
    total: z.number().int().nonnegative().nullable(),
    startedAt: z.iso.datetime().nullable(),
    ratePerMin: z.number().nonnegative().nullable(),
    etaSec: z.number().int().nonnegative().nullable(),
    /** Số lời gọi HTTP của lượt đang chạy (hoặc lượt gần nhất khi không chạy). */
    requestsThisRun: z.number().int().nonnegative(),
    /** Lần chạy tới — giữa hai job của lần nhập đầu là lúc job kế được nhận. */
    nextRunAt: z.iso.datetime().nullable(),
    runs: z.array(SyncProgressRun),
});
/**
 * Trạng thái hiển thị của một kết nối (§7.3 — dòng "Còn hoạt động?"), suy ra
 * theo thứ tự ưu tiên: ngắt → nguồn tắt → key hỏng → tạm dừng → chờ duyệt dự án →
 * đang nhập lần đầu → nhập xong (`once`) → lỗi → hoạt động.
 */
export const SyncConnectionStatus = z.enum([
    'active',
    'initial_import',
    'imported',
    'paused',
    'error',
    'key_invalid',
    'provider_disabled',
    'pending_approval',
    'disconnected',
]);
export const SyncConnectionView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    provider: z.object({
        id: z.uuid(),
        kind: SyncProviderKind,
        name: z.string(),
        enabled: z.boolean(),
        /**
         * Mốc chu kỳ tối thiểu của nguồn — để ô chu kỳ ở Cấu hình dự án không đưa
         * ra giá trị mà `PATCH` sẽ từ chối (APM-SYNC-i). Đọc ở đây chứ không từ
         * `GET /sync-providers`: danh sách ấy chỉ có nguồn **đang bật**.
         */
        minIntervalMinutes: z.number().int(),
        /**
         * APM-SYNC2-c — ô của "Đổi thông tin xác thực" và loại thông tin nào xám.
         * Cùng lý do với `minIntervalMinutes`: nguồn bị tắt không có trong
         * `GET /sync-providers`. `null` khi bản cài không còn adapter cho loại này.
         */
        capabilities: z.lazy(() => SyncProviderCapabilities).nullable(),
    }),
    externalProject: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
        /** Link mở dự án bên kia, nếu dựng được. */
        url: z.string().nullable(),
    }),
    /**
     * Bốn ký tự cuối của key — key không bao giờ rời server.
     * @deprecated APM-SYNC2-a — dùng `credentialHint`; giữ cho giao diện cũ tới `-c`.
     */
    apiKeyLast4: z.string(),
    /** Gợi ý thông tin xác thực theo loại (`••••a1b2`, `pm@acme.com · ••••a1b2`). */
    credentialHint: z.string(),
    direction: SyncDirection,
    intervalMinutes: z.number().int(),
    entities: SyncEntities,
    initialScope: SyncInitialScope,
    sprintColumns: SyncSprintColumns,
    isPrimary: z.boolean(),
    status: SyncConnectionStatus,
    pausedAt: z.iso.datetime().nullable(),
    disconnectedAt: z.iso.datetime().nullable(),
    /**
     * Đã **giải phóng** dự án ngoài (APM-SYNC-release): kết nối đã ngắt và không
     * còn giữ khoá, nên dự án ngoài nối được sang dự án khác. `false` với kết nối
     * đang sống lẫn kết nối chỉ ngắt mềm (vẫn giữ chỗ để nối lại).
     */
    released: z.boolean(),
    createdAt: z.iso.datetime(),
    /** Trạng thái vận hành (§4.2) — `null` khi chưa chạy lượt nào. */
    state: z
        .object({
        nextRunAt: z.iso.datetime().nullable(),
        runningSince: z.iso.datetime().nullable(),
        initialDoneAt: z.iso.datetime().nullable(),
        lastRunAt: z.iso.datetime().nullable(),
        lastRunOutcome: SyncRunOutcome.nullable(),
        lastErrorCode: z.string().nullable(),
        lastErrorMessage: z.string().nullable(),
        consecutiveFailures: z.number().int(),
        lastPullChangeAt: z.iso.datetime().nullable(),
        lastPushAt: z.iso.datetime().nullable(),
    })
        .nullable(),
    lastPull: SyncRunView.nullable(),
    lastPush: SyncRunView.nullable(),
    /**
     * Lượt mà `state` trỏ tới — khối "Đồng bộ" ở tab Điều lệ (APM-SYNC-h, §7.3):
     * `lastRun` cho dòng "Lần cập nhật cuối" (kết quả + số thay đổi),
     * `lastPullChange` / `lastPushChange` cho "Lần lấy dữ liệu mới cuối" / "Lần
     * đẩy ra ngoài cuối" — lượt **có thay đổi** gần nhất, không phải lượt gần
     * nhất (`lastPull`/`lastPush`, thường là "không có gì mới"). `null` khi chưa
     * có, hoặc lượt ấy đã bị dọn khỏi nhật ký (30 ngày).
     */
    lastRun: SyncRunView.nullable(),
    lastPullChange: SyncRunView.nullable(),
    lastPushChange: SyncRunView.nullable(),
    /**
     * Tiến độ nhập lần đầu (§5.7 — "Đang nhập lần đầu: 1.000/5.000"), chỉ khi
     * chưa nhập xong **và** đã biết tổng (sau trang đầu tiên). `scanned` đếm việc
     * **đã đọc** từ nguồn (trang × 100, chặn ở `total`), không phải việc đã tạo:
     * với "chỉ việc chưa xong" số việc tạo ra luôn ít hơn tổng của nguồn.
     */
    initialProgress: z
        .object({
        scanned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    })
        .nullable(),
    /**
     * Việc bên trong mà lượt đối soát tuần (APM-SYNC-j, plan §1 "Xoá") không còn
     * thấy ở nguồn — API ngoài không trả việc đã xoá, nên đây là cách duy nhất
     * biết. **Không** tự xoá: khối Điều lệ hiện "n việc không còn ở nguồn" để
     * người quyết. `count` chỉ đếm việc bên trong chưa xoá; `items` ≤ 20, mới
     * phát hiện trước.
     */
    missing: z.object({
        count: z.number().int().nonnegative(),
        items: z.array(z.object({
            taskId: z.uuid(),
            /** Mã việc bên trong (`APM-12`). */
            code: z.string().nullable(),
            title: z.string(),
            /** Mã việc bên nguồn lúc còn thấy (`WEB-42`). */
            externalCode: z.string().nullable(),
            missingSince: z.iso.datetime(),
        })),
    }),
    /**
     * APM-SYNC2-f — ưu tiên / ước lượng ở **trường tuỳ chỉnh** của nguồn
     * (`container.extra.fieldMap`), chỉ có nghĩa khi `provider.capabilities.priority`
     * / `estimate` là `custom_field` (Trello). `null` = chưa ghép.
     */
    fieldMap: z
        .lazy(() => SyncFieldMap)
        .nullable()
        .optional(),
    /** Trường tuỳ chỉnh của nguồn theo bản cache danh mục gần nhất — để chọn ở trên. */
    customFields: z.array(z.lazy(() => SyncExternalCustomField)).optional(),
});
/**
 * `GET /projects/:projectId/sync-connections/tasks/:taskId` — dòng "Đồng bộ với
 * <nguồn> · WEB-42 ↗" ở chi tiết task (APM-SYNC-h, §7.4).
 *
 * Bọc trong object: "không đồng bộ" là một giá trị (`sync: null`), không phải
 * body rỗng (bẫy "Endpoint trả `null` trần").
 */
export const TaskSyncLinkView = z.object({
    sync: z
        .object({
        /** Tên nguồn do quản trị đặt (vd "AI Service"). */
        source: z.string(),
        /** Mã việc bên nguồn (`WEB-42`); `null` khi chưa biết. */
        externalCode: z.string().nullable(),
        /** Link mở việc bên nguồn — chỉ khi nguồn có `web_base_url`. */
        url: z.string().nullable(),
        /** Kết nối đã ngắt: liên kết còn nhưng không đồng bộ nữa. */
        disconnected: z.boolean(),
    })
        .nullable(),
});
/** Key của dự án ngoài (`pmk_...`). Không bao giờ đi qua query string (§8). */
const SyncApiKey = z.string().trim().min(8).max(300);
const SyncSecret = z.string().trim().min(1).max(500);
/** Jira Cloud: chỉ `https://<tên>.atlassian.net` — plan-sync-providers §0, §8 (SSRF). */
export const JiraSiteUrl = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .transform((raw) => raw.replace(/\/+$/, ''))
    .refine((raw) => /^https:\/\/[a-z0-9][a-z0-9-]{0,62}\.atlassian\.net$/i.test(raw), {
    message: 'Site Jira phải có dạng https://<tên>.atlassian.net',
});
/**
 * Thông tin xác thực của **một kết nối** — union theo loại nguồn,
 * docs/plan-sync-providers.md §2.3. Lưu mã hoá nguyên khối ở
 * `project_sync_connection.credentials_enc` (`SyncCredentialsCodec`), không bao
 * giờ trả ra API, không bao giờ đi qua query string (§8).
 *
 * `lark_tasks` không có gì của người dùng: dùng app của nguồn + quyền đã cấp
 * qua OAuth (§6.2). `meegle.email` là người đứng tên thao tác (tra ra `user_key`).
 */
export const SyncCredentials = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('aiservice'), apiKey: SyncApiKey }),
    z.object({
        kind: z.literal('jira_cloud'),
        siteUrl: JiraSiteUrl,
        email: z.string().trim().min(3).max(254),
        apiToken: SyncSecret,
    }),
    z.object({ kind: z.literal('redmine'), apiKey: SyncSecret }),
    z.object({ kind: z.literal('trello'), apiKey: SyncSecret, token: SyncSecret }),
    z.object({
        kind: z.literal('lark_tasks'),
        /**
         * APM-SYNC2-g — lượt đăng nhập Lark (OAuth) vừa xong, id mờ do
         * `POST /project-sync/oauth/start` cấp. Chỉ cần ở bước chọn danh sách / xem
         * trước / kết nối; **không** lưu cùng kết nối (lượt chạy dùng tenant token).
         */
        grant: z
            .string()
            .trim()
            .regex(/^[A-Za-z0-9_-]{20,100}$/)
            .optional(),
    }),
    z.object({
        kind: z.literal('meegle'),
        /**
         * APM-SYNC2-h — email Meegle của người đứng tên thao tác. Adapter tra `user_key`
         * từ email (`POST /open_api/user/query`); plugin token đóng vai bất kỳ ai nên API
         * chỉ nhận email **trùng** email tài khoản ChillPM của người đang kết nối.
         */
        email: z
            .string()
            .trim()
            .min(3)
            .max(254)
            .regex(/^[^\s@]+@[^\s@]+$/, { message: 'Email không hợp lệ' }),
    }),
]);
/**
 * Dự án / board / danh sách bên ngoài mà kết nối trỏ tới — lưu ở
 * `project_sync_connection.container` (§2.3). `extra` là của adapter (Jira:
 * `projectStyle`, `fieldIds`…; Meegle: `workItemType`, `flow`), engine không đọc.
 */
export const SyncContainer = z.object({
    id: z.string().trim().min(1).max(64),
    key: z.string().max(64).optional(),
    name: z.string().max(200),
    url: z.string().max(500).optional(),
    extra: z.record(z.string(), z.unknown()),
});
/**
 * Ghép trường tuỳ chỉnh của nguồn ⇄ trường của ta — `container.extra.fieldMap`
 * (APM-SYNC2-b, plan-sync-providers §2.4). Chỉ cho nguồn mà `capabilities.priority`
 * / `estimate` là `custom_field`: kết nối chọn trường nào mang ưu tiên/ước lượng.
 *
 * `priority.values`: giá trị bên kia (chuỗi như API trả — id option hoặc tên) →
 * ưu tiên của ta. Đẩy ra dùng **khoá đầu tiên** trỏ tới ưu tiên ấy. Giá trị không
 * có trong bảng ⇒ không áp (giữ bên trong).
 */
export const SyncFieldMap = z.object({
    priority: z
        .object({
        fieldId: z.string().trim().min(1).max(120),
        values: z.record(z.string().max(200), TaskPriority),
    })
        .optional(),
    /** Trường số, đơn vị giờ. */
    estimate: z.object({ fieldId: z.string().trim().min(1).max(120) }).optional(),
});
/** Tài khoản bên ngoài mà thông tin xác thực đứng tên (`verify`, §2.3). */
export const ExternalAccount = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string().nullable(),
});
/**
 * Hai cách gửi thông tin xác thực: `credentials` (union theo loại) hoặc — **một
 * đợt**, cho giao diện cũ của aiservice — `apiKey` trần, API hiểu là
 * `{ kind: 'aiservice', apiKey }`. Đúng một trong hai.
 */
const CredentialsFields = {
    credentials: SyncCredentials.optional(),
    /** @deprecated APM-SYNC2-a — dùng `credentials`; giữ một đợt cho aiservice. */
    apiKey: SyncApiKey.optional(),
};
function requireOneCredential(value, ctx) {
    const count = (value.credentials ? 1 : 0) + (value.apiKey ? 1 : 0);
    if (count !== 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['credentials'],
            message: 'Gửi đúng một trong hai: `credentials` hoặc `apiKey`',
        });
    }
}
/** Id container bên ngoài đã chọn (bước chọn dự án/board) — nguồn một-key-một-dự án bỏ qua. */
const ContainerId = z.string().trim().min(1).max(64);
export const CreateSyncConnectionInput = z
    .object({
    providerId: z.uuid(),
    ...CredentialsFields,
    containerId: ContainerId.optional(),
    direction: SyncDirection.default('two_way'),
    intervalMinutes: SyncIntervalMinutes.default(DEFAULT_SYNC_INTERVAL_MINUTES),
    entities: SyncEntities.default(DEFAULT_SYNC_ENTITIES),
    initialScope: SyncInitialScope.default('all'),
    sprintColumns: SyncSprintColumns.default('all'),
    isPrimary: z.boolean().default(true),
    /** §5.8 — người ghép được theo email mà chưa ở dự án thì thêm vào. */
    addMatchedMembers: z.boolean().default(true),
})
    .superRefine(requireOneCredential);
export const UpdateSyncConnectionInput = z
    .object({
    direction: SyncDirection,
    intervalMinutes: SyncIntervalMinutes,
    entities: SyncEntities,
    sprintColumns: SyncSprintColumns,
    isPrimary: z.boolean(),
    /**
     * APM-SYNC2-f — trường tuỳ chỉnh nào mang ưu tiên / ước lượng (nguồn
     * `custom_field`, plan-sync-providers §2.4). `null` = bỏ ghép.
     */
    fieldMap: SyncFieldMap.nullable(),
})
    .partial();
/**
 * `POST /project-sync/projects` — tạo dự án **và** kết nối trong một transaction
 * (§6.2): không bao giờ ra một dự án "nửa đồng bộ".
 *
 * `project.workflowStatuses` không nhận từ client: workflow dựng theo danh sách
 * trạng thái mà server đọc được từ nguồn bằng chính key này (Q2).
 */
export const CreateSyncProjectInput = z.object({
    project: CreateProjectInput.omit({ workflowStatuses: true }),
    sync: CreateSyncConnectionInput,
    /**
     * APM-SYNC2-c (plan-sync-providers §2.6) — nhóm của từng cột mà người tạo
     * **xác nhận** ở bước xem trước, theo id trạng thái ngoài. Chỉ có nghĩa với
     * nguồn `statusCategories ≠ native` (Trello list, Lark section, Redmine): với
     * nguồn có nhóm sẵn, nhóm của nguồn là sự thật và trường này bị bỏ qua. Nguồn
     * không nhóm mà còn cột chưa có nhóm ⇒ 400, không đoán thay người dùng.
     */
    statusCategories: z
        .record(z.string().min(1).max(64), WorkflowCategory)
        .refine((value) => Object.keys(value).length <= 500, {
        message: 'Quá nhiều trạng thái',
    })
        .optional(),
});
export const CreateSyncProjectResult = z.object({
    projectId: z.uuid(),
    connectionId: z.uuid(),
    /** `pending_approval` ⇒ lượt nhập đầu tự chờ tới khi dự án được duyệt (§5.7). */
    projectStatus: ProjectStatus,
    /** Số người ghép được theo email vừa được thêm vào dự án. */
    addedMembers: z.number().int(),
});
/** `POST …/:cid/run` — "Đồng bộ ngay". */
export const SyncRunRequestResult = z.object({
    queued: z.literal(true),
    kind: SyncRunKind,
});
/**
 * `PUT …/key` — đổi thông tin xác thực; phải trỏ **cùng** container bên ngoài.
 * `credentials` cùng loại với nguồn, hoặc `apiKey` trần (aiservice, một đợt).
 */
export const RotateSyncKeyInput = z.object(CredentialsFields).superRefine(requireOneCredential);
/** Tên theo §2.6 ("Đổi thông tin xác thực") — cùng schema. */
export const RotateSyncCredentialsInput = RotateSyncKeyInput;
/**
 * `POST /project-sync/containers` — danh sách dự án/board/danh sách mà thông
 * tin xác thực thấy được (bước 2 của §2.6). **Không ghi gì**; quyền như xem
 * trước. `cursor` mờ, lấy từ `nextCursor` của trang trước.
 */
export const SyncContainersInput = z
    .object({
    providerId: z.uuid(),
    ...CredentialsFields,
    /** Có ⇒ thêm nguồn cho dự án có sẵn (`project:update` trên dự án đó). */
    projectId: z.uuid().optional(),
    q: z.string().trim().max(100).optional(),
    cursor: z.string().max(2000).optional(),
})
    .superRefine(requireOneCredential);
/**
 * `POST /project-sync/oauth/start` — APM-SYNC2-g, plan-sync-providers §6.2. Nguồn
 * `capabilities.oauthConnect` (Lark Tasks): người dùng đăng nhập ở nguồn trong
 * cửa sổ bật lên; `grant` đi vào `credentials` của các bước sau. Quyền như xem
 * trước.
 */
export const SyncOauthStartInput = z.object({
    providerId: z.uuid(),
    projectId: z.uuid().optional(),
});
export const SyncOauthStartResult = z.object({
    /** Trang đăng nhập của nguồn — mở trong cửa sổ bật lên. */
    authorizeUrl: z.string(),
    /** Id mờ của lượt đăng nhập — `credentials.grant`. Sống 30 phút, chỉ trong RAM. */
    grant: z.string(),
});
/** Trang tĩnh mà callback OAuth đưa cửa sổ bật lên về (`apps/web/public`). */
export const SYNC_OAUTH_DONE_PATH = '/sync-oauth-done.html';
/** `postMessage` của trang ấy gửi cho cửa sổ mở nó: `{ type, status }`. */
export const SYNC_OAUTH_MESSAGE = 'apm-sync-oauth';
/** `status` của trang ấy: xong / `state` lạ hoặc hết hạn / nguồn từ chối. */
export const SyncOauthDoneStatus = z.enum(['ok', 'state', 'denied', 'error']);
/**
 * `POST /project-sync/containers/create` — APM-SYNC2-g: ChillPM tạo **danh sách
 * mới** ở nguồn rồi trao quyền chủ cho người vừa đăng nhập (nhánh lùi của §6.2).
 * Chỉ nguồn `oauthConnect`; ghi ở nguồn, không ghi gì ở ChillPM.
 */
export const CreateSyncContainerInput = z
    .object({
    providerId: z.uuid(),
    ...CredentialsFields,
    projectId: z.uuid().optional(),
    name: z.string().trim().min(1).max(100),
})
    .superRefine(requireOneCredential);
export const SyncContainersPage = z.object({
    /** Tài khoản bên ngoài đứng tên — "Đăng nhập với tư cách <tên>". */
    account: ExternalAccount,
    items: z.array(SyncContainer),
    nextCursor: z.string().nullable(),
});
// ── Ghép (mapping, §5.8) ────────────────────────────────────────────────────
export const SyncMappingKind = z.enum(['user', 'status', 'task_type', 'sprint']);
/** Ghép tự động hay tay — để giao diện gắn nhãn. */
export const SyncMappingSource = z.enum(['auto', 'manual', 'none']);
export const SyncMappingEntry = z.object({
    kind: SyncMappingKind,
    externalId: z.string(),
    externalName: z.string(),
    /** Email (người) hoặc nhóm (trạng thái) hoặc trạng thái sprint ngoài. */
    externalHint: z.string().nullable(),
    /** `null` = không ghép. */
    localId: z.uuid().nullable(),
    source: SyncMappingSource,
    /**
     * Chỉ cho `user` (APM-SYNC-l): số việc **đang ở nguồn** mà người này là người
     * phụ trách chính, đếm từ `project_sync_link.base.assigneeExt` của lần đồng bộ
     * thành công cuối (việc đã biến mất ở nguồn không tính). Người chưa ghép thì
     * chừng ấy việc về ChillPM **không có người phụ trách**.
     */
    assignedTaskCount: z.number().int().nonnegative().optional(),
    /**
     * Số việc **đang đặt tạm** vì giá trị này chưa ghép (APM-SYNC-run) — chỉ có ở
     * `status` và `task_type`. Ghép xong rồi "Áp dụng lại" thì về 0.
     */
    pendingTaskCount: z.number().int().nonnegative().optional(),
    /**
     * APM-SYNC2-b (plan-sync-providers §2.4): gợi ý **chưa áp** — chỉ có khi chưa
     * ghép (`source = 'none'`). Người không email: theo tên hiển thị (không dấu, khớp
     * toàn phần rồi gần đúng). Trạng thái: cột đầu cùng nhóm — nhóm sẵn của nguồn,
     * rồi từ khoá trong tên, rồi vị trí cột (đầu → todo, cuối → done, giữa →
     * in_progress). **Không bao giờ tự áp** (APM-SYNC-run): giao diện điền sẵn, người
     * bấm Lưu mới thành ghép tay.
     */
    suggestion: z
        .object({
        localId: z.uuid().nullable(),
        localName: z.string().nullable(),
        /** Nhóm gợi ý của trạng thái ngoài (chỉ `status`). */
        category: WorkflowCategory.nullable(),
        /** `category` = nhóm sẵn của nguồn → cột đầu cùng nhóm (chỉ gợi ý, APM-SYNC-run). */
        reason: z.enum(['name', 'name_partial', 'category', 'position']),
    })
        .optional(),
});
/**
 * `GET …/mappings` — mọi thứ bên ngoài (theo bản cache danh mục) kèm ghép hiện
 * hành: tay (`manual`) thắng tự động (`auto`, gợi ý theo §5.8); `none` = không
 * ghép được.
 */
export const SyncMappingsView = z.object({
    entries: z.array(SyncMappingEntry),
    /** Lúc danh mục bên ngoài được làm mới lần cuối. */
    lookupsRefreshedAt: z.iso.datetime().nullable(),
});
export const PutSyncMappingsInput = z.object({
    mappings: z
        .array(z.object({
        kind: SyncMappingKind,
        externalId: z.string().trim().min(1).max(64),
        /** `null` = "đừng ghép". */
        localId: z.uuid().nullable(),
    }))
        .max(2000),
});
/**
 * `POST …/:cid/members/add-mapped` — APM-SYNC-l: người trong tổ chức đang được
 * ghép (tay hoặc tự động) mà chưa ở dự án ⇒ thêm với vai mặc định. Gọi lại
 * không thêm trùng (`added: 0`).
 */
export const AddSyncMappedMembersResult = z.object({ added: z.number().int().nonnegative() });
// ── Chuẩn hoá (§5.2) ────────────────────────────────────────────────────────
/**
 * Một việc ở hình chung của hai phía, **trong không gian id ngoài** — giá trị bên
 * trong được dịch sang id ngoài trước khi so, để không phải dịch hai chiều.
 *
 * Mọi trường tuỳ chọn: trường mà `SyncEntities` tắt thì **vắng mặt** (không so,
 * không ghi), khác với có mặt và bằng `null` (= "trống").
 */
export const NormalizedTask = z
    .object({
    title: z.string(),
    /**
     * **Markdown** như `RichTextEditor` lưu `task.description` (APM-SYNC-e): HTML của
     * nguồn đổi sang markdown ở adapter, rồi chuẩn hoá khoảng trắng.
     */
    description: z.string().nullable(),
    /** Id trạng thái ngoài. */
    statusExt: z.string(),
    /** Id người phụ trách chính ở ngoài. */
    assigneeExt: z.string().nullable(),
    /** Người phụ trách phụ bên ngoài — ChillPM chỉ có một, nên đẩy phải giữ họ (§2). */
    extraAssigneesExt: z.array(z.string()),
    priority: TaskPriority,
    startDate: CalendarDate.nullable(),
    dueDate: CalendarDate.nullable(),
    /** Hai chữ số thập phân. */
    estimateHours: z.number().nullable(),
    sprintExt: z.string().nullable(),
    parentExt: z.string().nullable(),
    taskTypeExt: z.string().nullable(),
})
    .partial();
// ── Danh mục bên ngoài + xem trước (§7.1) ───────────────────────────────────
export const SyncExternalSprintState = z.enum(['upcoming', 'running', 'closed']);
/** Một trường tuỳ chỉnh của nguồn (`SyncLookups.customFields`). */
export const SyncExternalCustomField = z.object({
    id: z.string(),
    name: z.string(),
    /** Kiểu như nguồn gọi (Trello: `list`, `number`, `text`, `date`, `checkbox`). */
    type: z.string(),
    /**
     * Lựa chọn của trường kiểu danh sách (Trello `list`, APM-SYNC2-f) — id lựa chọn
     * là giá trị mà việc mang (`ExternalTask.customFields`), tức khoá của
     * `SyncFieldMap.priority.values`.
     */
    options: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
});
/**
 * Bản cache danh mục bên ngoài (`project_sync_state.lookups`).
 *
 * APM-SYNC2-a (plan-sync-providers §2.3): `category` **nullable** — Trello list,
 * Lark section, Redmine (chỉ `is_closed`) không có nhóm; `position`/`isClosed`
 * cho bước gợi ý nhóm (`-b`). Trường mới đều tuỳ chọn: bản cache ghi trước đó
 * vẫn đọc được.
 */
export const SyncLookups = z.object({
    members: z.array(z.object({ id: z.string(), name: z.string(), email: z.string().nullable() })),
    /** Theo **thứ tự cột** bên ngoài. */
    statuses: z.array(z.object({
        id: z.string(),
        name: z.string(),
        category: WorkflowCategory.nullable(),
        position: z.number().int().optional(),
        isClosed: z.boolean().optional(),
    })),
    taskTypes: z.array(z.object({ id: z.string(), name: z.string() })),
    sprints: z.array(z.object({
        id: z.string(),
        name: z.string(),
        state: SyncExternalSprintState.nullable(),
        /**
         * Ngày của sprint nếu nguồn có (Jira Agile: `startDate`/`endDate`, ISO hoặc
         * `yyyy-MM-dd`) — APM-SYNC2-d. Vắng ⇒ engine đoán từ ngày của việc như trước.
         */
        startDate: z.string().nullable().optional(),
        endDate: z.string().nullable().optional(),
    })),
    /** Danh sách ưu tiên của nguồn (Jira/Redmine tuỳ cấu hình); vắng = nguồn có bộ cố định hoặc không có. */
    priorities: z
        .array(z.object({ id: z.string(), name: z.string(), local: TaskPriority.nullable() }))
        .optional(),
    /** Trường tuỳ chọn của nguồn — để kết nối chọn trường nào là ưu tiên/ước lượng (`-b`). */
    customFields: z.array(SyncExternalCustomField).optional(),
});
export const SyncPreviewInput = z
    .object({
    providerId: z.uuid(),
    ...CredentialsFields,
    containerId: ContainerId.optional(),
    /** Có ⇒ thêm nguồn cho dự án có sẵn (`project:update` trên dự án đó). */
    projectId: z.uuid().optional(),
})
    .superRefine(requireOneCredential);
/** Ghép dự kiến của một thứ bên ngoài trong bước xem trước. */
const PreviewMatch = z.object({
    externalId: z.string(),
    externalName: z.string(),
    localId: z.uuid().nullable(),
    localName: z.string().nullable(),
    /** `matched` ghép được · `default` dùng mặc định · `create` sẽ tạo mới · `none` không ghép. */
    outcome: z.enum(['matched', 'default', 'create', 'none']),
});
/**
 * `POST /project-sync/preview` — **không ghi gì**. ≈ 11 request bên ngoài (dự án 1,
 * danh mục 4, đếm theo nhóm 3, việc gần nhất 1–2, tag + trường tuỳ chỉnh 2).
 */
export const SyncPreview = z.object({
    externalProject: z.object({
        id: z.string(),
        key: z.string(),
        name: z.string(),
        keyName: z.string().nullable(),
    }),
    tasks: z.object({
        /**
         * `null` = nguồn không đếm được (`capabilities.totals = false` — Jira không
         * trả `total`, Lark Tasks phải quét toàn bộ): giao diện hiện "không rõ",
         * không hiện 0 (APM-SYNC2-c).
         */
        total: z.number().int().nullable(),
        /** `null` = nguồn không nhóm trạng thái, hoặc không đếm theo nhóm được. */
        byCategory: z
            .object({
            todo: z.number().int(),
            in_progress: z.number().int(),
            done: z.number().int(),
        })
            .nullable(),
        /** 10 việc cập nhật gần nhất, để người dùng nhận ra đúng dự án. */
        recent: z.array(z.object({
            code: z.string(),
            title: z.string(),
            statusName: z.string(),
            assigneeName: z.string().nullable(),
            updatedAt: z.iso.datetime(),
        })),
    }),
    statuses: SyncLookups.shape.statuses,
    members: z.array(PreviewMatch.extend({ email: z.string().nullable() })),
    taskTypes: z.array(PreviewMatch),
    sprints: z.array(PreviewMatch),
    /** Thứ không đồng bộ, kèm số đếm — giao diện nói lý do (§1). */
    unsupported: z.object({ tags: z.number().int(), customFields: z.number().int() }),
    /** Số request đã tốn cho bước xem trước. */
    requests: z.number().int(),
});
//# sourceMappingURL=project-sync.js.map